import React, { useEffect } from 'react';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenContactModal: () => void;
}

const ModalBase: React.FC<React.PropsWithChildren<Omit<ModalProps, 'onOpenContactModal'> & { title: string }>> = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[60]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between items-center p-6 border-b border-red-200 sticky top-0 bg-red-100 rounded-t-xl">
          <h2 className="text-2xl font-bold text-red-800">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-3xl p-1"
            aria-label="Cerrar modal"
          >
            &times;
          </button>
        </header>
        <main className="p-8 overflow-y-auto text-gray-600 leading-relaxed">
            {children}
        </main>
      </div>
    </div>
  );
};

export const JuzgadosEuModal: React.FC<ModalProps> = ({ isOpen, onClose, onOpenContactModal }) => {
  const handleContactClick = () => {
    onClose();
    onOpenContactModal();
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} title="Juzgados.EU - Representación Legal">
      <p className="mb-6">
        Cuando la vía administrativa se agota o la gravedad del caso lo requiere, es fundamental contar con una defensa jurídica experta en los tribunales. En Juzgados.EU, nuestros abogados especialistas en derecho de la circulación y contencioso-administrativo te representan con la máxima firmeza y dedicación para proteger tus derechos.
      </p>

      <section className="mb-6">
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Áreas de Especialización Judicial</h3>
        <p>Llevamos tu defensa en una amplia variedad de procedimientos judiciales en toda Europa:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong>Recursos Contencioso-Administrativos:</strong> Llevamos a los tribunales las resoluciones de la Administración que agotan la vía administrativa para buscar su anulación.</li>
          <li><strong>Defensa en Juicios Penales:</strong> Asistencia en delitos contra la seguridad vial como alcoholemias, excesos de velocidad punibles, conducción sin carnet o conducción temeraria.</li>
          <li><strong>Reclamaciones por Accidentes:</strong> Luchamos para conseguir la máxima indemnización por daños personales y materiales derivados de un accidente de tráfico.</li>
          <li><strong>Litigios contra Aseguradoras:</strong> Te defendemos frente a las compañías de seguros que rechazan o minimizan la cobertura de un siniestro.</li>
          <li><strong>Conflictos con Talleres o Fabricantes:</strong> Reclamaciones judiciales por reparaciones defectuosas, incumplimiento de garantías o vicios ocultos en vehículos.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Nuestra Estrategia Procesal</h3>
        <ol className="list-decimal list-inside mt-2 space-y-2">
          <li><strong>Análisis Jurídico Profundo:</strong> Estudiamos cada detalle de tu caso para identificar los fundamentos legales y probatorios más sólidos.</li>
          <li><strong>Diseño de la Estrategia de Defensa:</strong> Trazamos la línea de defensa o acusación más efectiva, preparando los escritos de demanda, contestación y demás actos procesales.</li>
          <li><strong>Representación en Sala:</strong> Nuestros abogados defienden tu caso con elocuencia y contundencia durante la vista oral.</li>
          <li><strong>Gestión de Recursos:</strong> Si la sentencia no es favorable, estudiamos y presentamos los recursos de apelación pertinentes ante instancias superiores.</li>
        </ol>
      </section>

      <section>
        <h3 className="text-xl font-semibold text-gray-700 mb-3">¿Por Qué Elegir a Juzgados.EU?</h3>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong>Abogados Especialistas:</strong> Equipo enfocado exclusivamente en derecho de la circulación y administrativo sancionador.</li>
          <li><strong>Enfoque Combativo:</strong> No nos conformamos. Luchamos por cada caso con la máxima determinación.</li>
          <li><strong>Comunicación Transparente:</strong> Te mantenemos informado en todo momento, explicando el proceso judicial en un lenguaje claro y comprensible.</li>
          <li><strong>Visión Integral:</strong> Coordinamos la defensa legal con los informes de nuestros expertos de Peritajes.EU para construir un caso inexpugnable.</li>
        </ul>
      </section>

      <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
        <Button onClick={handleContactClick} variant="primary" size="large">
          Contactar con un Abogado
        </Button>
      </div>
    </ModalBase>
  );
};